import axios from 'axios';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import React, { useEffect, useState, useContext } from 'react';
import { ModalContext } from 'app/app';
import { Download } from 'feather-icons-react';
import CW from './images/coverwhale_logo_white.png';
import { IconedButton } from 'components/IconedButton';
import { Icon, Loader, useAPI, ViewContext } from 'components/lib';
import { Form, Formik } from 'formik';
import { Edit, FileText } from 'feather-icons-react/build/IconComponents';
import styles from './styles.module.scss';
import DateUtils from 'helpers/dateUtils';
import * as Tooltip from "@radix-ui/react-tooltip";
import { EditableField } from "../../../components/editableField";
import NumberUtils from "../../../helpers/numberUtils";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../../../css/quillCustom.css';
import { TemplateButton } from 'components/TemplateButton';

const dateUtils = new DateUtils();

const bindIssue = [
  { value: "Bind Pending Signatures and/or Down Payment" },
  { value: "UW Declined" },
  { value: "CW Needs to Issue Policy Manually" },
  { value: "Address in Wrong State" },
  { value: "Major Violation by One or More Drivers" },
  { value: "MVR No Hit" },
  { value: "MVR Not Matching" },
  { value: "UW Review" },
  { value: "Cover Badger Review" },
  { value: "Ineligible Cargo" },
  { value: "Ineligible Operation" },
  { value: "FMCSA Scores High" },
  { value: "Too Many Cancellations" },
  { value: "Insured Does Home Deliveries" },
  { value: "Insured Visits Construction Sites" },
  { value: "Insured Shares Vehicles, Drivers and Equipment" },
  { value: "Insured Needs to make payment" },
  { value: "Pre-Bind Docs Signatures pending" },
  { value: "Proof of Prior Insurance" },
  { value: "Post-Bind Docs Signatures Pending" },
  { value: "Connect ELD" },
  { value: "Provide 3 years of Loss Runs (AL Only)" },
  { value: "Other" },
];

export default function RequestedBindDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const context = useContext(ViewContext);
  const { setModal } = useContext(ModalContext);
  const user = useAPI('/api/user');
  const account = useAPI('/api/account');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [bindReqData, setBindReqData] = useState(null);
  const [selectedIssues, setSelectedIssues] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const previousPage = searchParams.get("page") || 1;
  const [agenciesOptions, setAgenciesOptions] = useState([]);
  const [agencyNames, setAgencyNames] = useState({});
  const [isCreatedOnClient, setIsCreatedOnClient] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [quoteDocuments, setQuoteDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [quoteDocumentsLoading, setQuoteDocumentsLoading] = useState(false);
  const numberUtils = new NumberUtils();

  // Combine both types of documents
  const allDocuments = [
    // Quote documents (from /api/document endpoint)
    ...quoteDocuments.map(doc => ({
      ...doc,
      source: 'quote',
      type: 'Quote Document',
      sender: 'Quote System',
      sendAt: doc.createdAt
    })),
    // Bind request documents (from messages)
    ...documents.map(doc => ({
      ...doc,
      source: 'bind_request',
      type: 'Message Attachment',
      name: doc.url.split('/').pop() || 'Document'
    }))
  ];

  async function fetchBindRequest() {
    setLoading(true);
    await axios
      .get(`/api/bind-requests/admin/${id}`)
      .then(async (res) => {
        const bindReqData = res.data.data;
        setBindReqData(bindReqData);
        if (bindReqData.isCreatedOnClient) {
          setIsCreatedOnClient(true);
        }
        if (bindReqData.issues) {
          setSelectedIssues(bindReqData.issues);
        }
      })
      .catch((err) => {
        setError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  const fetchDocuments = async () => {
    setDocumentsLoading(true);
    try {
      const response = await axios.get(`/api/bind-requests/${id}/documents`);
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      context.notification.show(
        'Failed to load documents',
        'error',
        true,
        'toast',
        'x-circle'
      );
    } finally {
      setDocumentsLoading(false);
    }
  };

  const fetchQuoteDocuments = async () => {
    if (!bindReqData?.quoteId) return;
    
    setQuoteDocumentsLoading(true);
    try {
      const response = await axios.get(`/api/document?page=1&search=&company_id=${bindReqData.quoteId}&status=active`);
      
      // Handle 304 responses - they contain data but aren't treated as "successful" by default
      if (response.status === 304 && response.data) {
        console.log('304 response with data detected, treating as success:', response.data);
      }
      
      // Handle both old and new response formats from the API change
      const documents = response.data.data?.documents || response.data.data?.data || response.data?.data || [];
      console.log('Documents fetched:', response.data);
      setQuoteDocuments(documents);
    } catch (error) {
      console.error('Error fetching quote documents:', error);
      context.notification.show(
        'Failed to load quote documents',
        'error',
        true,
        'toast',
        'x-circle'
      );
    } finally {
      setQuoteDocumentsLoading(false);
    }
  };

  useEffect(() => {
    fetchBindRequest();
    fetchDropdownData();
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (bindReqData?.quoteId) {
      fetchQuoteDocuments();
    }
  }, [bindReqData?.quoteId]);

  const fetchDropdownData = async () => {
    try {
      // Use the same endpoint as the bind request modal for consistency
      const response = await axios.get("/api/account/all/minimal");
      const agencies = response.data.data;

      const sortedAgencies = agencies
        .map((agency) => ({
          label: agency.name.trim(),
          value: agency.id,
        }))
        .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));

      setAgenciesOptions(sortedAgencies);

      const agencyNames = agencies.reduce((acc, agency) => {
        acc[agency.id] = agency.name;
        return acc;
      }, {});
      setAgencyNames(agencyNames);
    } catch (error) {
      console.error(error);
      context.notification.show(
        "Failed to load quote",
        "error",
        true,
        "toast",
        "x-circle"
      );
    }
  }

  const handleIssueChange = (issue) => {
    setSelectedIssues((prevSelectedIssues) => {
      const issueObject = { value: issue };
      if (prevSelectedIssues.some((i) => i.value === issue)) {
        return prevSelectedIssues.filter((i) => i.value !== issue);
      } else {
        return [...prevSelectedIssues, issueObject];
      }
    });
  };

  const handleEmailUpdate = async (field, selectedValue) => {
    let emailValue = '';

    if (field === 'agency') {
      const agencyId = selectedValue;
      if (!agencyId) return;

      try {
        const { data } = await axios.get(`/api/account/${agencyId}`);
        const agentEmail = data.data.owner_email;

        await handleUpdateField('assignedAgentEmail', agentEmail);
      } catch (error) {
        console.warn('Error fetching agent email:', error);
      }
    }
  };


  const handleUpdateField = async (field, value) => {
    try {
      const response = await axios.put(`/api/bind-requests/${id}`, {
        [field]: value,
      });

      if (response.status !== 200) {
        throw new Error('Failed to update field');
      }

      setBindReqData((prevData) => ({
        ...prevData,
        [field]: value,
      }));
    } catch (error) {
      context.notification.show(
        'There was a problem when updating the field.',
        'error',
        true,
        'toast',
        'x-circle'
      );
    }
  };


  const handleNextClick = async () => {
    if (selectedIssues.length === 0) {
      context.notification.show(
        'Please select at least one issue before continuing.',
        'error',
        true,
        'toast',
        'x-circle'
      );
      return;
    } else {
      await axios
        .put(`/api/bind-requests/${id}`, { issues: selectedIssues })
        .then(() => {
          context.notification.show(
            'Issues sent successfully!',
            'success',
            true,
            'toast',
            'check-circle'
          );
        })
        .catch((error) => {
          context.notification.show(
            'There was a problem when sending the selected issues.',
            'error',
            true,
            'toast',
            'x-circle'
          );
          console.error('Error sending issues: ', error);
        })
        .finally(() => {
          setModal({
            name: 'bind_request_issue_modal',
            data: {
              assignedAgentEmail: bindReqData.assignedAgentEmail,
            },
          });
        });
    }
  };

  useEffect(() => {
    if (
      bindReqData &&
      (bindReqData.issues !== undefined || null)
    ) {
      if (
        bindReqData.issues.length > 0 &&
        !bindReqData.messages
      ) {
        setModal({
          name: 'bind_request_issue_modal',
          data: {
            assignedAgentEmail: bindReqData.assignedAgentEmail,
          },
        });
      }
    }
  }, [setModal, bindReqData]);

  const handleSubmit = async (values) => {
    setSending(true);
    try {
      if (!bindReqData.assignedAgentEmail) {
        context.notification.show(
          'No agent email assigned to this bind request. Please assign an agent email first.',
          'error',
          true,
          'toast',
          'x-circle'
        );
        setSending(false);
        return;
      }

      const formData = new FormData();
      formData.append('sender', 'Cover Badger Processing Team');
      formData.append('receiver', bindReqData.assignedAgentEmail);
      formData.append('title', values.title);
      formData.append('description', values.description);
      [...selectedFiles].forEach((file, i) => {
        formData.append(`documents`, file, file.name);
      });

      const response = await axios.put(
        `/api/bind-requests/${id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      if (response.data.success) {
        context.notification.show(
          'Message sent successfully!',
          'success',
          true,
          'toast',
          'check-circle'
        );
        setModal('');
      }
    } catch (error) {
      setSending(false);
      context.notification.show(
        error.response?.data?.message || error.message ||
        'There was a problem when sending the message.',
        'error',
        true,
        'toast',
        'x-circle'
      );
    } finally {
      fetchBindRequest();
      fetchDocuments();
      if (bindReqData?.quoteId) {
        fetchQuoteDocuments();
      }
      setSending(false);
    }
  };

  const handleUpdateBindIssue = async (issueId, solved) => {
    try {
      const updatedIssues = bindReqData.issues.map((issue) =>
        issue.value === issueId ? { ...issue, solved } : issue
      );

      const response = await axios.put(`/api/bind-requests/${id}`, {
        issues: updatedIssues,
      });

      if (response.status !== 200) {
        throw new Error('Failed to update bind request issue');
      }

      setBindReqData((prevData) => ({
        ...prevData,
        issues: updatedIssues,
      }));
      window.location.reload();
    } catch (error) {
      context.notification.show(
        'There was a problem when updating the issue.',
        'error',
        true,
        'toast',
        'x-circle'
      );
    }
  };

  if (user.loading || loading) return <Loader />;
  if (error) {
    return <div>Error loading bind request data: {error.message}</div>;
  }
  if (!bindReqData) {
    return <div>No bind request data available</div>;
  }

  return (
    <>
      {bindReqData &&
        (bindReqData.issues.length === 0 ||
          !bindReqData.messages) ? (
        <>
          <div className="w-full flex items-center justify-left">
            <IconedButton
              size="130px"
              color="#d32425"
              textColor="white"
              icon="arrow-left"
              text="Go back"
              action={() => {
                navigate(`/admin/requestedbind?page=${previousPage}`);
              }}
            />
          </div>

          <div
            className="mb-6 xl:w-1/2 sm:w-3/4 xs:w-full mx-auto bg-white pb-6 max-w-screen-xl"
            id="14"
          >
            <header className="bg-black flex items-center justify-between px-4 pb-2 pt-2">
              <img src={CW} alt="Cover Whale" className="w-[360px]" />
            </header>

            <div className="flex-col border rounded p-4 mt-4 pt-0 border-slate-300 mx-6">
              <div className="flex justify-between py-2 border-b border-slate-300">
                <div>Bind Request Number</div>
                <div>{bindReqData.bindNumber}</div>
              </div>
              {bindReqData && (
                <>
                  {isCreatedOnClient ? (
                    <>
                      <div className="flex justify-between py-2 border-b border-slate-300">
                        <div>Submission / Policy</div>
                        <div>{bindReqData.submissionNumber}</div>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-300">
                        <div>Agency Name</div>
                        <div>{bindReqData.agencyName}</div>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-300">
                        <div>Agent email</div>
                        <div>{bindReqData.assignedAgentEmail}</div>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-300">
                        <div>Name of insured</div>
                        <div>{bindReqData.companyName}</div>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-300">
                        <div>Effective Date</div>
                        <div>
                          {bindReqData.effectiveDate
                            ? new Date(bindReqData.effectiveDate).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                            })
                            : 'N/A'}
                        </div>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-300">
                        <div>Total Cost</div>
                        <div>{numberUtils.formatToCurrency(bindReqData.totalCost)}</div>
                      </div>
                      <div className="space-y-2">
                        {[
                          {
                            label: "answerQuestion1",
                            question:
                              'Does the insured do any home deliveries including but not limited to moving operations?',
                            answer: bindReqData.answerQuestion1,
                          },
                          {
                            label: "answerQuestion2",
                            question: 'Does the insured visit construction sites at any time as part of their operation?',
                            answer: bindReqData.answerQuestion2,
                          },
                          {
                            label: "answerQuestion3",
                            question:
                              'Please provide in detail, what the insured hauls (General freight is not considered detailed)',
                            answer: bindReqData.answerQuestion3,
                          },
                          {
                            label: "answerQuestion4",
                            question:
                              'Is the insured connected to any other entity or share drivers, equipment or vehicles with any other entity?',
                            answer: bindReqData.answerQuestion4,
                          },
                        ].map((item, index) => (
                          <div key={index} className="flex justify-between py-2 border-b border-slate-300">
                            <div className="w-3/5">{item.question}</div>
                            <Tooltip.Provider>
                              <Tooltip.Root>
                                <Tooltip.Trigger
                                  className="max-w-[200px] truncate text-ellipsis overflow-hidden cursor-pointer hover:underline">
                                  {item.answer}
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                  <Tooltip.Content className="max-w-[400px] bg-white p-3 shadow-lg rounded-lg border">
                                    {item.answer}
                                  </Tooltip.Content>
                                </Tooltip.Portal>
                              </Tooltip.Root>
                            </Tooltip.Provider>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <EditableField
                        label="Submission / Policy"
                        value={bindReqData.submissionNumber}
                        onSave={(value) => handleUpdateField('submissionNumber', value)}
                      />
                      <EditableField
                        label="Agency Name"
                        value={bindReqData.agencyName}
                        onSave={(newAgencyId) => {
                          const selectedAgency = agenciesOptions.find((agency) => agency.value === newAgencyId);
                          if (selectedAgency) {
                            handleUpdateField('agencyName', selectedAgency.label);
                            handleEmailUpdate('agency', newAgencyId);
                            setBindReqData((prev) => ({
                              ...prev,
                              agencyId: newAgencyId,
                              agencyName: selectedAgency.label,
                            }));
                          }
                        }}
                        options={agenciesOptions.map((agency) => ({
                          label: agency.label,
                          value: agency.value,
                        }))}
                      />
                      <EditableField
                        label="Agent Email"
                        value={bindReqData.assignedAgentEmail}
                        onSave={(value) => handleUpdateField('assignedAgentEmail', value)}
                      />
                      <EditableField
                        label="Name of Insured"
                        value={bindReqData.companyName}
                        onSave={(value) => handleUpdateField('companyName', value)}
                      />
                      <EditableField
                        label="Effective Date"
                        value={bindReqData.effectiveDate || 'N/A'}
                        onSave={(value) => handleUpdateField('effectiveDate', value)}
                      />
                      <EditableField
                        label="Total Cost"
                        value={bindReqData.totalCost || 0}
                        onSave={(value) => handleUpdateField('totalCost', value)}
                      />
                      {[
                        {
                          label: "answerQuestion1",
                          question:
                            'Does the insured do any home deliveries including but not limited to moving operations?',
                          answer: bindReqData?.answerQuestion1 ?? "",
                        },
                        {
                          label: "answerQuestion2",
                          question:
                            "Does the insured visit construction sites at any time as part of their operation?",
                          answer: bindReqData?.answerQuestion2 ?? "",
                        },
                        {
                          label: "answerQuestion3",
                          question:
                            "Please provide in detail, what the insured hauls (General freight is not considered detailed)",
                          answer: bindReqData?.answerQuestion3 ?? "",
                        },
                        {
                          label: "answerQuestion4",
                          question:
                            "Is the insured connected to any other entity or share drivers, equipment or vehicles with any other entity?",
                          answer: bindReqData?.answerQuestion4 ?? "",
                        },
                      ].map((item, index) => {
                        return (
                          <div key={index} className="flex justify-between py-2 border-b border-slate-300">
                            <div className="w-3/5">{item.question}</div>
                            <div className="w-2/5 flex items-center">
                              <EditableField
                                value={item.answer ?? ""}
                                onSave={(value) => handleUpdateField(item.label, value)}
                                isQuestion
                              />
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </>
              )}
            </div>

            <div className="p-4 pl-6 mt-4 pt-0">
              Select issues that need to be addressed:
            </div>

            <div className="flex-col border rounded p-4 pt-0 border-slate-300 mx-6">
              {bindIssue.map((issue, index) => (
                <div
                  key={index}
                  className="flex justify-between py-2 border-b border-slate-300"
                >
                  <div>{issue.value}</div>
                  <input
                    type="checkbox"
                    checked={selectedIssues.some(
                      (i) => i.value === issue.value
                    )}
                    onChange={() => handleIssueChange(issue.value)}
                  />
                </div>
              ))}
            </div>

            {/* Documents Section */}
            <div className="p-4 pl-6 mt-4 pt-0">
              All documents (quote & bind request):
            </div>

            <div className="flex-col border rounded p-4 pt-0 border-slate-300 mx-6">
              {(documentsLoading || quoteDocumentsLoading) ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                  <span className="ml-2">Loading documents...</span>
                </div>
              ) : allDocuments.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                  <FileText size={48} color="#ccc" />
                  <h3 className="mt-2 text-lg font-medium">No Documents Found</h3>
                  <p className="text-sm">No documents have been attached to this bind request yet.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-300">
                        <th className="text-left py-2 px-2">Type</th>
                        <th className="text-left py-2 px-2">Name</th>
                        <th className="text-left py-2 px-2">Source</th>
                        <th className="text-left py-2 px-2">Date</th>
                        <th className="text-left py-2 px-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allDocuments.map((doc, index) => (
                        <tr key={index} className="border-b border-slate-200">
                          <td className="py-2 px-2">
                            <div className="flex items-center">
                              <FileText size={16} color="#637381" />
                              <span className="ml-2">{doc.type}</span>
                            </div>
                          </td>
                          <td className="py-2 px-2">
                            <div className="text-sm text-gray-600">
                              {doc.name || 'Document'}
                            </div>
                          </td>
                          <td className="py-2 px-2">
                            <div className="text-sm text-gray-600">
                              {doc.source === 'quote' ? 'Quote System' : doc.sender}
                            </div>
                          </td>
                          <td className="py-2 px-2">
                            <div className="text-sm text-gray-600">
                              {doc.sendAt ? new Date(doc.sendAt).toLocaleDateString() : 'N/A'}
                            </div>
                          </td>
                          <td className="py-2 px-2">
                            <div className="flex items-center">
                              <button
                                onClick={() => {
                                  if (doc.source === 'quote' && doc.s3_link) {
                                    window.open(doc.s3_link, '_blank');
                                  } else {
                                    window.open(doc.url, '_blank');
                                  }
                                }}
                                className="text-red-600 hover:text-red-800 transition-colors"
                                title="Download document"
                              >
                                <Download size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="p-4 mt-4 pt-0">
              <div className="w-full flex items-center">
                <IconedButton
                  size="1500px"
                  color="#d32425"
                  textColor="white"
                  text="Next"
                  action={() => handleNextClick()}
                />
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="w-full flex items-center justify-left">
            <IconedButton
              size="130px"
              color="#d32425"
              textColor="white"
              icon="arrow-left"
              text="Go back"
              action={() => {
                navigate(`/admin/requestedbind?page=${previousPage}`);
              }}
            />
          </div>
          <main className="flex-1 flex flex-col md:flex-row gap-4 max-h-full mt-2">
            {/* left */}
            <div className="w-full md:w-1/2">
              <div
                className="mb-6 rounded-[12px] w-full mx-auto bg-white pb-6 max-w-screen-xl"
                id="14"
              >
                <header
                  className="bg-black flex items-center justify-between px-4 pb-2 pt-2 rounded-t-[12px]">
                  <img
                    src={CW}
                    alt="Cover Whale"
                    className="w-[360px]"
                  />
                </header>

                <div className="flex-col border rounded p-4 mt-4 pt-0 border-slate-300 mx-6">
                  <div className="flex justify-between py-2 border-b border-slate-300">
                    <div>Bind Request Number</div>
                    <div>{bindReqData.bindNumber}</div>
                  </div>
                  {isCreatedOnClient ? (
                    <>
                      <div className="flex justify-between py-2 border-b border-slate-300">
                        <div>Submission / Policy</div>
                        <div>{bindReqData.submissionNumber}</div>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-300">
                        <div>Agency Name</div>
                        <div>{bindReqData.agencyName}</div>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-300">
                        <div>Agent email</div>
                        <div>{bindReqData.assignedAgentEmail}</div>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-300">
                        <div>Agent Phone</div>
                        <div>{bindReqData.agencyPhone || "N/A"}</div>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-300">
                        <div>Name of insured</div>
                        <div>{bindReqData.companyName}</div>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-300">
                        <div>Total Cost</div>
                        <div>{numberUtils.formatToCurrency(bindReqData.totalCost)}</div>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-300">
                        <div>Effective Date</div>
                        <div>
                          {bindReqData.effectiveDate
                            ? new Date(bindReqData.effectiveDate).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                            })
                            : 'N/A'}
                        </div>
                      </div>
                      <div className="space-y-2">
                        {[
                          {
                            question: 'Does the insured do any home deliveries including but not limited to moving operations?',
                            answer: bindReqData.answerQuestion1,
                          },
                          {
                            question: 'Does the insured visit construction sites at any time as part of their operation?',
                            answer: bindReqData.answerQuestion2,
                          },
                          {
                            question: 'Please provide in detail, what the insured hauls (General freight is not considered detailed)',
                            answer: bindReqData.answerQuestion3,
                          },
                          {
                            question: 'Is the insured connected to any other entity or share drivers, equipment or vehicles with any other entity?',
                            answer: bindReqData.answerQuestion4,
                          },
                        ].map((item, index) => (
                          <div key={index} className="flex justify-between py-2 border-b border-slate-300">
                            <div className="w-3/5">{item.question}</div>
                            <Tooltip.Provider>
                              <Tooltip.Root>
                                <Tooltip.Trigger
                                  className="max-w-[200px] truncate text-ellipsis overflow-hidden cursor-pointer hover:underline">
                                  {item.answer}
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                  <Tooltip.Content className="max-w-[400px] bg-white p-3 shadow-lg rounded-lg border">
                                    {item.answer}
                                  </Tooltip.Content>
                                </Tooltip.Portal>
                              </Tooltip.Root>
                            </Tooltip.Provider>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <EditableField
                        label="Submission / Policy"
                        value={bindReqData.submissionNumber}
                        onSave={(value) => handleUpdateField('submissionNumber', value)}
                      />
                      <EditableField
                        label="Agency Name"
                        value={bindReqData.agencyName}
                        onSave={(newAgencyId) => {
                          const selectedAgency = agenciesOptions.find((agency) => agency.value === newAgencyId);
                          if (selectedAgency) {
                            handleUpdateField('agencyName', selectedAgency.label);
                            handleEmailUpdate('agency', newAgencyId);
                            setBindReqData((prev) => ({
                              ...prev,
                              agencyId: newAgencyId,
                              agencyName: selectedAgency.label,
                            }));
                          }
                        }}
                        options={agenciesOptions.map((agency) => ({
                          label: agency.label,
                          value: agency.value,
                        }))}
                      />
                      <EditableField
                        label="Agent Email"
                        value={bindReqData.assignedAgentEmail}
                        onSave={(value) => handleUpdateField('assignedAgentEmail', value)}
                      />
                      <EditableField
                        label="Name of Insured"
                        value={bindReqData.companyName}
                        onSave={(value) => handleUpdateField('companyName', value)}
                      />
                      <EditableField
                        label="Effective Date"
                        value={bindReqData.effectiveDate || 'N/A'}
                        onSave={(value) => handleUpdateField('effectiveDate', value)}
                      />
                      <EditableField
                        label="Total Cost"
                        value={bindReqData.totalCost || 0}
                        onSave={(value) => handleUpdateField('totalCost', value)}
                      />
                      <div className="space-y-2">
                        {[
                          {
                            label: "answerQuestion1",
                            question:
                              'Does the insured do any home deliveries including but not limited to moving operations?',
                            answer: bindReqData?.answerQuestion1 ?? "",
                          },
                          {
                            label: "answerQuestion2",
                            question:
                              "Does the insured visit construction sites at any time as part of their operation?",
                            answer: bindReqData?.answerQuestion2 ?? "",
                          },
                          {
                            label: "answerQuestion3",
                            question:
                              "Please provide in detail, what the insured hauls (General freight is not considered detailed)",
                            answer: bindReqData?.answerQuestion3 ?? "",
                          },
                          {
                            label: "answerQuestion4",
                            question:
                              "Is the insured connected to any other entity or share drivers, equipment or vehicles with any other entity?",
                            answer: bindReqData?.answerQuestion4 ?? "",
                          },
                        ].map((item, index) => {
                          return (
                            <div key={index} className="flex justify-between py-2 border-b border-slate-300">
                              <div className="w-3/5">{item.question}</div>
                              <div className="w-2/5 flex items-center">
                                <EditableField
                                  value={item.answer}
                                  onSave={(value) => handleUpdateField(item.label, value)}
                                  isQuestion
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>

                <div className="p-4 pl-6 mt-4 pt-0">
                  Check an issue to mark it as solved:
                </div>

                <div className="flex-col border rounded p-4 pt-0 border-slate-300 mx-6">
                  {bindReqData.issues.map((issue, index) => (
                    <div
                      key={index}
                      className="flex justify-between py-2 border-b border-slate-300"
                    >
                      <div className="inline-flex items-center">
                        {issue.value}
                      </div>
                      <div className="inline-flex items-center">
                        <label className={styles.label}>
                          <span className={styles.checkbox_container}>
                            <input
                              type="checkbox"
                              className={styles.checkbox}
                              checked={issue.solved}
                              onChange={(e) => {
                                const isChecked = e.target.checked;
                                handleUpdateBindIssue(
                                  issue.value,
                                  isChecked
                                );
                              }}
                              disabled={
                                bindReqData.ticketStatus ===
                                'Completed'
                              }
                            />
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className={styles.icon}
                              viewBox="0 0 20 20"
                              fill="currentColor"
                              stroke="currentColor"
                              strokeWidth="1"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              ></path>
                            </svg>
                          </span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Documents Section */}
                <div className="p-4 pl-6 mt-4 pt-0">
                  All documents (quote & bind request):
                </div>

                <div className="flex-col border rounded p-4 pt-0 border-slate-300 mx-6">
                  {(documentsLoading || quoteDocumentsLoading) ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                      <span className="ml-2">Loading documents...</span>
                    </div>
                  ) : allDocuments.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                      <FileText size={48} color="#ccc" />
                      <h3 className="mt-2 text-lg font-medium">No Documents Found</h3>
                      <p className="text-sm">No documents have been attached to this bind request yet.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-slate-300">
                            <th className="text-left py-2 px-2">Type</th>
                            <th className="text-left py-2 px-2">Name</th>
                            <th className="text-left py-2 px-2">Source</th>
                            <th className="text-left py-2 px-2">Date</th>
                            <th className="text-left py-2 px-2">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allDocuments.map((doc, index) => (
                            <tr key={index} className="border-b border-slate-200">
                              <td className="py-2 px-2">
                                <div className="flex items-center">
                                  <FileText size={16} color="#637381" />
                                  <span className="ml-2">{doc.type}</span>
                                </div>
                              </td>
                              <td className="py-2 px-2">
                                <div className="text-sm text-gray-600">
                                  {doc.name || 'Document'}
                                </div>
                              </td>
                              <td className="py-2 px-2">
                                <div className="text-sm text-gray-600">
                                  {doc.source === 'quote' ? 'Quote System' : doc.sender}
                                </div>
                              </td>
                              <td className="py-2 px-2">
                                <div className="text-sm text-gray-600">
                                  {doc.sendAt ? new Date(doc.sendAt).toLocaleDateString() : 'N/A'}
                                </div>
                              </td>
                              <td className="py-2 px-2">
                                <div className="flex items-center">
                                  <button
                                    onClick={() => {
                                      if (doc.source === 'quote' && doc.s3_link) {
                                        window.open(doc.s3_link, '_blank');
                                      } else {
                                        window.open(doc.url, '_blank');
                                      }
                                    }}
                                    className="text-red-600 hover:text-red-800 transition-colors"
                                    title="Download document"
                                  >
                                    <Download size={16} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {bindReqData.ticketStatus === 'Completed' ? null : (
                  <div className="p-4 mt-4 pt-0">
                    <div className="w-full flex items-center">
                      <IconedButton
                        size="1500px"
                        color="#d32425"
                        textColor="white"
                        text="Add"
                        action={() => {
                          setModal({
                            name: 'add_bind_request_issue_modal',
                            data: {
                              status: bindReqData.status,
                              issues: bindReqData.issues,
                            },
                          });
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* right */}
            <div className="w-full md:w-1/2">
              <div className="w-full bg-white flex items-center justify-center rounded-[12px]">
                <div className="w-full mt-[20px] mb-[20px]">
                  <div className="max-h-[800px] flex flex-col">
                    <div
                      className="w-full flex justify-between items-center mb-4 pb-2 px-4 border-b border-slate-300">
                      <div>
                        <p className="font-bold">
                          {bindReqData.agencyName}
                        </p>
                        <p className="text-sm">
                          {bindReqData.assignedAgentEmail}
                        </p>
                        <p className="text-sm">
                          {dateUtils.timestamp(bindReqData.updatedAt)}
                        </p>
                      </div>

                      {bindReqData.ticketStatus ===
                        'Completed' ? null : (
                        <button
                          className="bg-[#d32425] text-white px-4 py-2 rounded-md disabled:bg-gray-200 disabled:cursor-not-allowed"
                          onClick={() => {
                            setModal({
                              name: 'bind_request_status_modal',
                              data: {
                                id: bindReqData.id,
                                status: bindReqData.status,
                                ticketStatus: bindReqData.ticketStatus,
                              },
                            });
                          }}
                          disabled={
                            bindReqData.ticketStatus === 'Completed'
                          }
                        >
                          <div className="flex justify-between items-center gap-2">
                            <Icon
                              className="icon"
                              image="check-square"
                              size="18"
                              color="light"
                            />
                            Select Status
                          </div>
                        </button>
                      )}
                    </div>

                    {/* Ticket messages */}
                    <div className="flex-1 overflow-y-scroll px-4 py-2 flex flex-col-reverse">
                      {bindReqData.messages
                        .slice()
                        .sort(
                          (a, b) =>
                            new Date(b.sendAt) - new Date(a.sendAt)
                        )
                        .map((message, index) => (
                          <div
                            key={index}
                            className="bg-gray-100 rounded-md p-2 mb-2 w-full"
                          >
                            <div id="title" className="mb-2 p-1">
                              Subject: {message.title}
                            </div>
                            <div
                              className="whitespace-pre-wrap p-1"
                              dangerouslySetInnerHTML={{ __html: message.description }}
                            />
                            <br />
                            <div className="w-full flex justify-between items-center p-1">
                              <div>{message.sender}</div>
                              <div>
                                {dateUtils.timestamp(message.sendAt)}
                              </div>
                            </div>
                            {message.documents.length > 0 && (
                              <div className="mt-2 mb-2">
                                <ul className="flex flex-wrap">
                                  {message.documents.map(
                                    (document, index) => {
                                      const urlParts =
                                        document.split('/');
                                      const fileNameWithTimestamp =
                                        urlParts[urlParts.length - 1];
                                      const fileName =
                                        fileNameWithTimestamp.split(
                                          '-'
                                        )[0];
                                      const decodedFileName =
                                        decodeURIComponent(fileName);
                                      const truncatedDocument =
                                        decodedFileName.length > 18
                                          ? decodedFileName.substring(
                                            0,
                                            18
                                          ) + '...'
                                          : decodedFileName;

                                      return (
                                        <label
                                          key={index}
                                          htmlFor="custom-input"
                                          className="
                                          text-md text-white mt-2 mr-2 py-2
                                          px-4 rounded-md border-0 bg-[#d32425]
                                          cursor-pointer max-w-[180px] flex
                                          justify-center items-center
                                        "
                                          title="click to download file"
                                        >
                                          <a
                                            href={document}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="
                                            text-white no-underline truncate
                                            flex items-center whitespace-nowrap
                                            overflow-hidden text-ellipsis
                                          "
                                          >
                                            <span
                                              style={{
                                                marginRight: '8px',
                                              }}
                                            >
                                              {truncatedDocument}
                                            </span>
                                            <Download
                                              className="text-white"
                                              size={16}
                                            />
                                          </a>
                                        </label>
                                      );
                                    }
                                  )}
                                </ul>
                              </div>
                            )}
                          </div>
                        ))}
                    </div>

                    {bindReqData.ticketStatus ===
                      'Completed' ? null : (
                      <Formik
                        initialValues={{
                          title: '',
                          description: '',
                          documents: [],
                        }}
                        onSubmit={handleSubmit}
                      >
                        {({
                          setFieldValue,
                          handleChange,
                          values,
                          setFieldTouched
                        }) => (
                          <Form encType="multipart/form-data">
                            <div className="w-full mb-4 px-4 py-2">
                              <div className="flex justify-between items-center mb-2">
                                <input
                                  type="text"
                                  className="flex-1 p-2 border border-slate-300 rounded-lg mr-2"
                                  placeholder="Subject..."
                                  name="title"
                                  value={values.title}
                                  onChange={handleChange}
                                />
                                <TemplateButton 
                                  toolType="bind_requests"
                                  onTemplateSelect={(template) => {
                                    setFieldValue('title', template.title);
                                    // Convert newlines to HTML breaks for ReactQuill
                                    const formattedMessage = template.message.replace(/\n/g, '<br>');
                                    setFieldValue('description', formattedMessage);
                                  }}
                                />
                              </div>

                              <ReactQuill
                                value={values.description}
                                onChange={(content) => {
                                  setFieldValue('description', content);
                                  setFieldTouched('description', true, false);
                                }}
                                modules={{
                                  toolbar: [
                                    ['bold', 'italic', 'underline', 'strike'],
                                    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                    ['clean']
                                  ],
                                }}
                                formats={[
                                  'bold', 'italic', 'underline', 'strike',
                                  'list', 'bullet'
                                ]}
                                placeholder="Write a message"
                              />

                              <ul className="flex flex-wrap">
                                {Array.from(selectedFiles).map(
                                  (file) => (
                                    <label
                                      htmlFor="custom-input"
                                      className="
                                    block text-md text-white mt-2 mr-2 py-2 px-4
                                    rounded-md border-0 bg-[#d32425]
                                    cursor-pointer w-[200px]
                                    "
                                      title="Click to change file"
                                    >
                                      <div className="flex items-center">
                                        <span
                                          style={{
                                            marginRight: '8px',
                                          }}
                                        >
                                          {file.name.length > 18
                                            ? file.name.substring(
                                              0,
                                              18
                                            ) + '...'
                                            : file.name}
                                        </span>
                                        <Edit
                                          className="text-white"
                                          size={16}
                                        />
                                      </div>
                                    </label>
                                  )
                                )}
                              </ul>
                            </div>

                            {bindReqData.ticketStatus ===
                              'Completed' ? null : (
                              <div className="w-full flex justify-between px-4 py-2">
                                <div className="flex flex-row items-center gap-4">
                                  <input
                                    type="file"
                                    id="custom-input"
                                    onChange={(event) => {
                                      setSelectedFiles(
                                        event.target.files
                                      );
                                    }}
                                    hidden
                                    multiple
                                    disabled={
                                      bindReqData.ticketStatus ===
                                      'Completed'
                                    }
                                  />
                                  <label
                                    htmlFor="custom-input"
                                    className="
                                      block text-md text-white py-2
                                      px-4 rounded-md border-0 bg-[#d32425]
                                      cursor-pointer disabled:cursor-not-allowed
                                      "
                                    disabled={
                                      bindReqData.ticketStatus ===
                                      'Completed'
                                    }
                                  >
                                    Attach one or more documents
                                  </label>
                                </div>

                                <button
                                  className="
                                    bg-[#d32425] text-white
                                    px-4 py-2 rounded-md disabled:bg-gray-200
                                    disabled:cursor-not-allowed
                                  "
                                  type="submit"
                                  disabled={
                                    sending ||
                                    bindReqData.ticketStatus ===
                                    'Completed' ||
                                    !values.title
                                  }
                                >
                                  {sending ? 'Sending...' : 'Send'}
                                </button>
                              </div>
                            )}
                          </Form>
                        )}
                      </Formik>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </main>
        </>
      )}
    </>
  );
}
